import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { ForceAssignDriverRequest, GetNearbyDriversForAssignItem } from '@shared/models';
import { SharedDeliveryOrderService } from '@shared/services/shared-delivery-order.service';
import { Utils } from 'app/utils';
import { MessageService } from 'primeng/api';

@Component({
	selector: 'force-assign-dialog',
	templateUrl: './force-assign-dialog.component.html',
	styleUrls: ['./force-assign-dialog.component.scss']
})
export class ForceAssignDialogComponent {
	private readonly defaultAvatarUrl = '/XpressApp/assets/images/avatar_placeholder.svg';

	@Input() visible: boolean = false;
	@Input() loading: boolean = false;
	@Input() drivers: GetNearbyDriversForAssignItem[] = [];
	@Input() isConnex: boolean = false;
	@Input() orderId?: number;

	@Output() visibleChange = new EventEmitter<boolean>();
	@Output() assignSuccess = new EventEmitter<void>();

	@ViewChild('stepper') stepper?: MatStepper;

	selectedDriver?: GetNearbyDriversForAssignItem;
	highlightDiscard: boolean = false;
	assignLoading: boolean = false;
	assignError: boolean = false;
	errorMessage: string = '';

	constructor(
		private deliveryOrderService: SharedDeliveryOrderService,
		private messageService: MessageService
	) { }

	close(): void {
		this.visible = false;
		this.selectedDriver = undefined;
		this.assignLoading = false;
		this.assignError = false;
		this.errorMessage = '';
		this.stepper?.reset();
		this.visibleChange.emit(false);
	}

	selectDriver(driver: GetNearbyDriversForAssignItem): void {
		this.selectedDriver = driver;
		this.assignError = false;
		this.errorMessage = '';
		this.stepper?.next();
	}

	discardConfirmation(): void {
		this.selectedDriver = undefined;
		this.assignError = false;
		this.errorMessage = '';
		this.stepper?.previous();
	}

	confirmAssign(): void {
		if (!this.selectedDriver || !this.orderId) {
			return;
		}

		this.assignLoading = true;
		this.assignError = false;
		this.errorMessage = '';

		const dto: ForceAssignDriverRequest = {
			OrderId: this.orderId,
			RiderId: this.selectedDriver.Id,
			TimeZoneOffset: Utils.getTimezoneOffset()
		};

		const assignRequest = this.isConnex
			? this.deliveryOrderService.assignScheduledOrderToRider(dto, this.messageService)
			: this.deliveryOrderService.offerOrderToRider(dto, this.messageService);

		assignRequest
			.then(data => {
				if (!data) {
					this.assignLoading = false;
					this.assignError = true;
					this.errorMessage = 'No response from server';
					return;
				}

				if (!data.Success) {
					this.assignLoading = false;
					this.assignError = true;
					this.errorMessage = data.ErrorMessage || 'Failed to assign driver';
					return;
				}

				// Success
				this.messageService.add({
					severity: 'success',
					summary: 'Driver Assigned',
					detail: `${this.selectedDriver?.FirstName} ${this.selectedDriver?.LastName} has been assigned to trip #${this.orderId}`
				});

				this.assignLoading = false;
				this.assignSuccess.emit();
				this.close();
			})
			.catch(err => {
				this.assignLoading = false;
				this.assignError = true;
				this.errorMessage = 'An error occurred while assigning the driver';
				throw err;
			});
	}

	retryAssign(): void {
		this.confirmAssign();
	}

	getDriverFullName(driver: GetNearbyDriversForAssignItem): string {
		return `${driver.FirstName} ${driver.LastName}`.trim();
	}

	getDriverImageUrl(driver: GetNearbyDriversForAssignItem): string {
		return driver.ImageUrl?.trim() || this.defaultAvatarUrl;
	}

	onDriverImageError(event: Event): void {
		const img = event.target as HTMLImageElement | null;
		if (!img) {
			return;
		}

		if (img.src.includes('avatar_placeholder.svg')) {
			img.onerror = null;
			return;
		}

		img.src = this.defaultAvatarUrl;
		img.onerror = null;
	}

	shouldShowWarning(driver: GetNearbyDriversForAssignItem): boolean {
		return !!driver.WarningMessage?.trim();
	}

	shouldShowDistance(driver: GetNearbyDriversForAssignItem): boolean {
		return !this.isConnex && !this.shouldShowWarning(driver);
	}

	onBackdropClick(): void {
		if (this.stepper?.selectedIndex === 0) {
			this.close();
		} else {
			this.highlightDiscard = true;
			setTimeout(() => {
				this.highlightDiscard = false;
			}, 300);
		}
	}
}

